import { Actions, reducer } from './reducer';
import {
	createContext,
	PropsWithChildren,
	ReactNode,
	useContext,
	useReducer,
} from 'react';
import {
	AgendarCitaFormParams,
	AgendarCitaState,
	ValidateDateParams,
	ValidateDoctorDateParams,
} from 'utils/interfaces';
import {
	API,
	getAvailableHours,
	getAvailableHours15minutes,
	getAvailableHoursDoctors,
} from 'utils';
import { CitaType } from '../../../utils/interfaces';

export const AgendarCitaInitialState: AgendarCitaState = {
	view: 'formView',
	clinics: [],
	schedules: [],
	categories: [],
	isFormLoading: false,
	showConfirmarCita: false,
	isDateTouched: false,
	doctors: [],
	phone: '',
  citaId: '',
};

interface ContextState extends AgendarCitaState {
	setTime: (time: string) => void;
	dispatch: React.Dispatch<Actions>;
	agendarCita: (correo: string, callback: () => void) => void;
	agendarCitaDoctor: (
		correo: string,
		callback: () => void,
		telefono?: string
	) => void;
	setShowConfirmarCita: (payload: boolean) => void;
	onDatePick: (params: ValidateDateParams) => Promise<void>;
	onDoctorDatePick: (params: ValidateDoctorDateParams) => Promise<void>;
	onFormSubmit: (
		params: AgendarCitaFormParams,
		callback: () => void
	) => Promise<void>;
	setCitaType: (citaType: CitaType) => void;
	isSpecialist: boolean;
}

const AgendarCitaContext = createContext({} as ContextState);

export function AgendarCitaProvider({
	children,
}: PropsWithChildren<ReactNode>) {
	const [state, dispatch] = useReducer(reducer, AgendarCitaInitialState);

	const setShowConfirmarCita = (payload: boolean) => {
		dispatch({ type: 'setShowConfirmarCita', payload });
	};

	const setTime = (payload: string) => {
		dispatch({ type: 'setTime', payload });
	};

	const setCitaType = (citaType: CitaType) => {
		dispatch({ type: 'setCitaType', payload: { citaType } });
	};

	const onFormSubmit = async (
		params: AgendarCitaFormParams,
		callback: () => void
	) => {
		dispatch({ type: 'setFormLoading', payload: true });
		try {
			const response = await API.validateAfiliado(params);
			if (response.status === 418) {
				dispatch({
					type: 'setStatusMessage',
					payload: response.data.mensaje,
				});
			}

			if (response.status === 200) {
				const patient = {
					...response.data,
					dui: params.dui,
					numeroAfiliado: params.afiliado,
				};
				delete patient.listEstablecimiento;
				dispatch({ type: 'setPatient', payload: { patient } });
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch({ type: 'setFormLoading', payload: false });
			callback();
		}
	};

	const agendarCita = async (correo: string, callback: () => void) => {
		const { selectedDate, selectedTime, selectedClinic, patient } = state;

		if (!selectedDate || !selectedTime || !selectedClinic || !patient) {
			return;
		}

		try {
			const response = await API.setCita({
				fecha: `${selectedDate} ${selectedTime}`,
				establecimiento: selectedClinic?.idEstablecimiento as string,
				afiliado: patient?.numeroAfiliado as string,
			});

			if (response.status === 418) {
				dispatch({
					type: 'setCrearCitaMessage',
					payload: response.data.message,
				});
			}

			if (response.status === 200) {
				if (correo) {
					const recordatorio = await API.setRecorgatorio({
						correo,
						cita: response.data.data,
					});

					if (recordatorio.status === 418) {
						console.error(
							'Error al guardar recordatorio ',
							recordatorio.data.mensaje
						);
					}

					if (recordatorio.status === 200) {
						dispatch({ type: 'setEmail', payload: correo });
					}
				}
        dispatch({ type: 'setCitaId', payload: response.data.data });
				dispatch({ type: 'setView', payload: 'confirmationView' });
			}
		} catch (error) {
			console.error('Error agendarCita', error);
		} finally {
			callback();
			setShowConfirmarCita(false);
		}
	};

	const agendarCitaDoctor = async (
		correo: string,
		callback: () => void,
		telefono?: string
	) => {
		const { selectedDate, selectedTime, selectedDoctor, patient } = state;

		if (!selectedDate || !selectedTime || !selectedDoctor || !patient) {
			return;
		}

		try {
			const response = await API.setCitaDoctor({
				fecha: `${selectedDate} ${selectedTime}`,
				establecimiento: selectedDoctor.idEstablecimiento,
				afiliado: patient?.numeroAfiliado as string,
				idMedico: selectedDoctor.idMedico,
				telefono,
			});

			if (response.status === 418) {
				dispatch({
					type: 'setCrearCitaMessage',
					payload: response.data.message,
				});
			}

			if (response.status === 200) {
				if (correo) {
					const recordatorio = await API.setRecorgatorio({
						correo,
						cita: response.data.data,
					});

					if (recordatorio.status === 418) {
						console.error(
							'Error al guardar recordatorio ',
							recordatorio.data.mensaje
						);
					}

					if (recordatorio.status === 200) {
						dispatch({ type: 'setEmail', payload: correo });
					}
				}
        dispatch({ type: 'setCitaId', payload: response.data.data });
				dispatch({ type: 'setView', payload: 'confirmationView' });
			}
		} catch (error) {
			console.error('Error agendarCita', error);
		} finally {
			callback();
			setShowConfirmarCita(false);
		}
	};

	const onDatePick = async (params: ValidateDateParams) => {
		dispatch({ type: 'setFormLoading', payload: true });
		dispatch({ type: 'setDate', payload: { date: params.fecha || '' } });
		try {
			const response = await API.validateDate(params);

			if (response.status === 200) {
				const schedulesResponse = response.data;
				const schedules =
					state.selectedClinic?.idTipo == '3' // Consultorio = 3, Policlinicios = 2
						? getAvailableHours(
                schedulesResponse,
                params.fecha || '',
                state.selectedClinic
              ) // Calculate available hours based on busy hours
						: getAvailableHours15minutes(
                schedulesResponse,
                params.fecha || '',
                state.selectedClinic
              ); // Calculate available hours based on busy hours by 15 minutes
				dispatch({ type: 'setSchedules', payload: { schedules } });
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch({ type: 'setFormLoading', payload: false });
		}
	};

	const onDoctorDatePick = async (params: ValidateDoctorDateParams) => {
		dispatch({ type: 'setFormLoading', payload: true });
		dispatch({ type: 'setDate', payload: { date: params.fecha || '' } });
		try {
			const response = await API.validateDoctorDate(params);

			if (response.status === 200) {
				const schedulesResponse = response.data;
				const schedules = getAvailableHoursDoctors(
					schedulesResponse,
					params.fecha || '',
					state.selectedCategory,
				);
				dispatch({ type: 'setSchedules', payload: { schedules } });
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch({ type: 'setFormLoading', payload: false });
		}
	};

	const isSpecialist = state.citaType === 'especialista';

	const value = {
		...state,
		setTime,
		dispatch,
		agendarCita,
		agendarCitaDoctor,
		onFormSubmit,
		setShowConfirmarCita,
		onDatePick,
		onDoctorDatePick,
		setCitaType,
		isSpecialist,
	};

	return (
		<AgendarCitaContext.Provider value={value}>
			{children}
		</AgendarCitaContext.Provider>
	);
}

export function useAgendarCita() {
	return useContext(AgendarCitaContext);
}
